import { ThemeEvents } from '@theme/events';

/**
 * KM Brandboard PDP: sync main image + thumbnail selection with variant updates; thumb clicks swap preview.
 */
class KmProductPdpGallery extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    const main = this.querySelector('[data-km-pdp-main-img]');
    this.querySelectorAll('[data-km-pdp-thumb]').forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const url = btn.getAttribute('data-full-src');
          const w = btn.getAttribute('data-full-width');
          if (!url || !(main instanceof HTMLImageElement)) return;
          main.src = url;
          if (w) {
            main.srcset = `${url} ${w}w`;
            main.sizes = '(min-width: 750px) 50vw, 100vw';
          } else {
            main.removeAttribute('srcset');
          }
          this.querySelectorAll('[data-km-pdp-thumb]').forEach((t) => t.classList.remove('is-active'));
          btn.classList.add('is-active');

          const mediaId = btn.getAttribute('data-media-id');
          this.#syncVariantSelectToMedia(mediaId);
        },
        { signal }
      );
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * When a thumbnail matches a variant’s featured media, keep the dropdown in sync (same as picking that variant).
   * @param {string | null} mediaId
   */
  #syncVariantSelectToMedia(mediaId) {
    if (mediaId == null || mediaId === '') return;
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker.km-pdp-variant-picker');
    if (!(picker instanceof HTMLElement)) return;

    const idStr = String(mediaId);

    for (const select of picker.querySelectorAll('select.km-pdp-variant-picker__select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const match = Array.from(select.options).find((o) => o.dataset.featuredMediaId === idStr);
      if (match && select.value !== match.value) {
        select.value = match.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    const mapEl = this.querySelector('[data-km-pdp-media-variants]');
    if (!mapEl?.textContent?.trim()) return;
    /** @type {{ mediaId: number, variantId: number, optionValues: string[] }[]} */
    let rows = [];
    try {
      rows = JSON.parse(mapEl.textContent);
    } catch {
      return;
    }
    const candidates = rows.filter((r) => String(r.mediaId) === idStr);
    if (!candidates.length) return;

    const wantedVid = new URLSearchParams(window.location.search).get('variant');
    let chosen =
      (wantedVid && candidates.find((r) => String(r.variantId) === wantedVid)) || candidates[0];

    const selects = Array.from(picker.querySelectorAll('select.km-pdp-variant-picker__select'));
    const vals = chosen.optionValues;
    if (!selects.length || !vals?.length) return;

    const n = Math.min(selects.length, vals.length);
    let changed = false;
    for (let i = 0; i < n; i++) {
      const sel = selects[i];
      const want = vals[i];
      const opt = Array.from(sel.options).find((o) => o.value === want);
      if (!opt || sel.value === want) continue;
      sel.value = want;
      for (const o of sel.options) {
        o.removeAttribute('selected');
      }
      opt.setAttribute('selected', 'selected');
      changed = true;
    }
    if (!changed) return;

    const last = selects[selects.length - 1];
    last.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const v = detail?.resource;
    const src = v?.featured_media?.preview_image?.src;
    const main = this.querySelector('[data-km-pdp-main-img]');
    if (src && main instanceof HTMLImageElement) {
      main.src = src;
      main.removeAttribute('srcset');
    }
    const mediaId = v?.featured_media?.id;
    if (mediaId == null) return;
    const idStr = String(mediaId);
    this.querySelectorAll('[data-km-pdp-thumb]').forEach((t) => {
      t.classList.toggle('is-active', t.getAttribute('data-media-id') === idStr);
    });
  };
}

if (!customElements.get('km-product-pdp-gallery')) {
  customElements.define('km-product-pdp-gallery', KmProductPdpGallery);
}
